import { V3Pill } from "@/components/OpsSectionHead";
import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { InfoTip } from "./InfoTip";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/exec-v2`;
export const Pill = V3Pill;
const Wait = ({ text }) => <div className="text-sm text-gray-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" />{text}</div>;

const niceMax = (m) => { const step = m <= 20 ? 5 : m <= 60 ? 10 : m <= 150 ? 25 : 50; return { top: Math.ceil(m / step) * step || step, step }; };
const fmtD = (iso) => { const [, m, d] = iso.split("-"); return `${+m}/${+d}`; };
const H = 150;

const WeekBar = ({ x, k, top }) => {
  const v = x[k]; const up = (v.delta_pct ?? 0) > -5;
  const color = x.state === "future" ? "bg-sky-200 border border-dashed border-sky-400" : x.state === "today" ? "keep-color v3-live-bar" : up ? "bg-emerald-500" : "bg-rose-400";
  return (
    <div className="flex-1 text-center" data-testid={`exec-weekday-bar-${x.label}`}>
      <div className="relative mx-auto w-full" style={{ height: H }}>
        {x.closed ? <div className="absolute inset-x-1 bottom-0 text-[10px] font-bold text-gray-400 bg-gray-50 rounded py-1" data-testid={`exec-weekday-closed-${x.label}`}>Closed</div> : <>
          {v.avg > 0 && <div className="absolute inset-x-0 border-t-2 border-dashed border-gray-500 z-10" style={{ bottom: (v.avg / top) * H }} title={`usual ${v.avg}`} />}
          <div className={`absolute inset-x-1 bottom-0 rounded-t-md transition-[height] duration-500 ${color}`} style={{ height: (v.last / top) * H }} />
          <div className="absolute inset-x-0 text-[11px] font-extrabold text-gray-800" style={{ bottom: (v.last / top) * H + 2 }}>{v.last}</div>
        </>}
      </div>
      <div className={`text-xs font-bold mt-1 ${x.state === "today" ? "text-[#1F8A7C]" : "text-gray-800"}`}>{x.label} {fmtD(x.date)}</div>
      <div className="text-[10px] text-gray-500 h-3.5">
        {x.state === "today" ? <span className="text-[#1F8A7C] font-bold">LIVE{x.checked_in != null ? ` · ${x.checked_in} in` : ""}</span>
          : x.state === "future" ? (x.closed ? "" : "booked so far")
          : v.delta_pct == null ? "" : <span className={up ? "text-emerald-600" : "text-rose-600"}>{v.delta_pct > 0 ? "+" : ""}{v.delta_pct}%</span>}
      </div>
    </div>
  );
};

// Daycare this week (Sun–Sat) vs each weekday's usual; refreshes in the background.
export const DaycareByWeekday = () => {
  const [d, setD] = useState(null);
  const [k, setK] = useState("all");
  useEffect(() => {
    let t;
    const load = (refresh) => { clearTimeout(t); return axios.get(`${API}/weekday${refresh === true ? "?refresh=true" : ""}`).then((r) => { setD(r.data); return r.data.status === "computing" || r.data.refreshing; })
      .catch(() => { setD((x) => x || { days: [] }); return false; })
      .then((busy) => { t = setTimeout(load, busy ? 8000 : 120000); }); };
    const onRefresh = () => load(true);
    load();
    window.addEventListener("exec:refresh", onRefresh);
    return () => { clearTimeout(t); window.removeEventListener("exec:refresh", onRefresh); };
  }, []);
  const days = d?.days || [];
  const goal = k === "all" ? d?.daycare_goal : null;
  const { top, step } = niceMax(Math.max(1, goal || 0, ...days.map((x) => Math.max(x[k]?.last || 0, x[k]?.avg || 0))));
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  return (
    <Card className="border-0 shadow-md p-4 sm:p-5" data-testid="exec-weekday">
      <div className="flex flex-wrap items-center gap-2">
        <Pill>Daycare This Week</Pill>
        {d?.week_start && <span className="text-sm font-semibold text-gray-600" data-testid="exec-weekday-range">{fmtD(d.week_start)} – {fmtD(d.week_end)}</span>}
        <InfoTip title="Daycare This Week" tid="info-weekday">
          <p>Each bar = daycare dogs on that day from Gingr (cancellations excluded), Sunday to Saturday. A new week starts automatically Sunday morning.</p>
          <p><b>Finished days</b>: green = at or above usual (or within 5% under), red = more than 5% under. The % under the day compares with usual.</p>
          <p><b>Today</b> (striped teal) = all daycare dogs booked today, live; "in" = checked in right now.</p>
          <p><b>Later days</b> (light blue) = booked so far; they fill in as bookings come.</p>
          <p><b>Dashed line (usual)</b> = average of the same weekday over the previous 7 weeks, skipping closed days{days[0] ? ` (e.g. Tue usual = ${days[2]?.[k]?.avg ?? "—"})` : ""}.</p>
          {goal > 0 && <p><b>Orange line (goal)</b> = your daily daycare goal of {goal} from Occupancy settings.</p>}
          <p className="text-gray-500">Updates in the background every 10 minutes.</p>
        </InfoTip>
        {d?.refreshing && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" data-testid="exec-weekday-refreshing" />}
        <div className="ml-auto flex text-xs font-bold bg-gray-100 rounded-full p-0.5" data-testid="exec-weekday-filter">
          {[["all", "All"], ["large", "Large"], ["small", "Small"]].map(([v, l]) => (
            <button key={v} onClick={() => setK(v)} data-testid={`exec-weekday-${v}`} className={`px-2.5 py-0.5 rounded-full ${k === v ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      {!d || d.status === "computing" ? <Wait text="Reading this week's daycare from Gingr…" /> : (
        <>
          <div className="flex gap-2 mt-5">
            <div className="relative w-7 shrink-0" style={{ height: H }} data-testid="exec-weekday-yaxis">
              {ticks.map((v) => <div key={v} className="absolute right-1 text-[10px] text-gray-400 -translate-y-1/2" style={{ top: H - (v / top) * H }}>{v}</div>)}
            </div>
            <div className="relative flex-1">
              {ticks.map((v) => <div key={v} className="absolute inset-x-0 border-t border-gray-100" style={{ top: H - (v / top) * H }} />)}
              {goal > 0 && <div className="absolute inset-x-0 z-20 border-t-2 border-amber-500 pointer-events-none" style={{ top: H - (goal / top) * H }} data-testid="exec-weekday-goal-line">
                <span className="absolute right-0 -top-4 text-[10px] font-extrabold text-amber-600 bg-white/80 px-1 rounded">Goal {goal}</span></div>}
              <div className="relative flex items-start gap-1.5 sm:gap-3">{days.map((x) => <WeekBar key={x.date} x={x} k={k} top={top} />)}</div>
            </div>
          </div>
          <div className="text-[11px] text-gray-400 mt-2 flex flex-wrap gap-x-3">
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500 align-middle" /> done (on/above usual)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-400 align-middle" /> done (below usual)</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm keep-color v3-live-bar align-middle" /> today, live</span>
            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-200 border border-dashed border-sky-400 align-middle" /> booked so far</span>
            <span>- - - usual (avg of same weekday, prior 7 weeks)</span>
            {goal > 0 && <span className="text-amber-600 font-semibold"><span className="inline-block w-4 border-t-2 border-amber-500 align-middle" /> daily goal {goal} (occupancy settings)</span>}
          </div>
        </>
      )}
    </Card>
  );
};

const Outlook = ({ months }) => (
  <Card className="border-0 shadow-md p-4" data-testid="exec-boarding-outlook">
    <div className="font-bold text-gray-900">Boarding outlook <span className="text-xs text-gray-400 font-normal">booked so far vs last year's actual</span></div>
    <div className="space-y-2 mt-3">
      {months.map((m) => (
        <div key={m.name} className="text-xs" data-testid={`exec-outlook-${m.name}`}>
          <div className="flex justify-between"><b>{m.name}</b><span>{m.boarding_pct ?? "—"}% <span className="text-gray-400">/ LY {m.boarding_ly ?? "—"}%</span></span></div>
          <div className="relative h-2 bg-gray-100 rounded mt-1">
            <div className="absolute inset-y-0 left-0 bg-indigo-500 rounded" style={{ width: `${Math.min(100, m.boarding_pct || 0)}%` }} />
            {m.boarding_ly != null && <div className="absolute -top-0.5 h-3 w-0.5 bg-gray-700" style={{ left: `${Math.min(100, m.boarding_ly)}%` }} />}
          </div>
        </div>
      ))}
    </div>
    <div className="text-[10px] text-gray-400 mt-2">Black tick = where last year finished. Future months keep filling as bookings come in.</div>
  </Card>
);

const AGENT_NAMES = { vaccine_reminders: "vaccine reminders", unsigned_agreements: "agreement chases" };
const Agents = ({ a }) => {
  const tiles = [
    ["lead follow-up texts", a.lead_texts], ["churn nudges sent", a.churn_nudges_sent], ["renewal texts", a.renewal_texts], ["boarding replies", a.request_replies],
    ...Object.entries(a.compliance_runs || {}).map(([k, v]) => [`${AGENT_NAMES[k] || k.replace(/_/g, " ")} runs`, v.runs]),
  ];
  return (
    <Card className="border-0 shadow-md p-4" data-testid="exec-agents">
      <div className="font-bold text-gray-900">Agents at work <span className="text-xs text-gray-400 font-normal">last 7 days</span></div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
        {tiles.map(([l, n]) => <div key={l} className="bg-gray-50 rounded-lg p-2"><b className="text-base text-gray-900">{n || 0}</b><div className="text-gray-500">{l}</div></div>)}
      </div>
    </Card>
  );
};

const OccTrend = ({ o }) => (
  <Card className="border-0 shadow-md p-4" data-testid="exec-occ-trend">
    <div className="font-bold text-gray-900">Occupancy over time <span className="text-xs text-gray-400 font-normal">nightly boarding % · last 90 days</span></div>
    <ResponsiveContainer width="100%" height={150}>
      <LineChart data={o.series} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
        <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(5)} interval={14} />
        <YAxis tick={{ fontSize: 9 }} domain={[0, 110]} />
        <Tooltip formatter={(v) => (v == null ? "—" : `${v}%`)} />
        <ReferenceLine y={90} stroke="#f87171" strokeDasharray="3 3" />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Line type="monotone" dataKey="boarding" name="Boarding" stroke="#6366f1" dot={false} strokeWidth={2} connectNulls />
        <Line type="monotone" dataKey="daycare" name="Daycare" stroke="#10b981" dot={false} strokeWidth={1.5} connectNulls />
        <Line type="monotone" dataKey="boarding_ly" name="Boarding LY" stroke="#cbd5e1" strokeDasharray="4 3" dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
    <div className="text-xs" data-testid="exec-occ-trend-summary">Avg boarding <b>{o.avg ?? "—"}%</b>{o.avg_ly != null && <> vs <b>{o.avg_ly}%</b> LY</>} · <b>{o.nights_over_90}</b> nights over 90%</div>
  </Card>
);

// Phase 2 row: boarding outlook, agents at work, occupancy over time.
export const ExecPhase2 = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/outlook`).then((r) => setD(r.data)).catch(() => setD((x) => x || {}));
    load();
    const id = setInterval(load, 300000);
    window.addEventListener("exec:refresh", load);
    return () => { clearInterval(id); window.removeEventListener("exec:refresh", load); };
  }, []);
  if (!d) return <Card className="border-0 shadow-md p-4"><Wait text="Loading outlook…" /></Card>;
  return (
    <div className="space-y-3"><Pill>Outlook &amp; Automation</Pill>
    <div className="grid lg:grid-cols-3 gap-4" data-testid="exec-phase2">
      <Outlook months={d.boarding_outlook || []} />
      <Agents a={d.agents || {}} />
      <OccTrend o={d.occupancy || { series: [] }} />
    </div></div>
  );
};
